import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, User, GraduationCap, Award, Briefcase, ClipboardList, Wallet, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "profile", label: "Profile & Skills", icon: User },
  { key: "training", label: "Training", icon: GraduationCap },
  { key: "certs", label: "Certifications", icon: Award },
  { key: "clients", label: "Clients", icon: Briefcase },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "earnings", label: "Earnings", icon: Wallet },
  { key: "ratings", label: "Ratings", icon: Star },
];

export default function FreelancerDashboard() {
  const [hash, setHash] = useState(window.location.hash.replace("#", "") || "overview");
  useEffect(() => {
    const on = () => setHash(window.location.hash.replace("#", "") || "overview");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const titles = { overview: "Overview", profile: "Profile & Skills", training: "Training", certs: "Certifications", clients: "Clients", tasks: "My Tasks", earnings: "Earnings", ratings: "Ratings" };
  return (
    <DashboardLayout nav={NAV} title={titles[hash]}>
      {hash === "overview" && <Overview />}
      {hash === "profile" && <Profile />}
      {hash === "training" && <TrainingLink />}
      {hash === "certs" && <Certs />}
      {hash === "clients" && <Clients />}
      {hash === "tasks" && <Tasks />}
      {hash === "earnings" && <Earnings />}
      {hash === "ratings" && <Ratings />}
    </DashboardLayout>
  );
}

function KPI({ label, value, tint = "blue" }) {
  const color = tint === "orange" ? "#FF6A00" : "#155EEF";
  return (
    <div className="card-soft p-6">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 font-heading font-bold text-3xl" style={{ color }}>{value}</div>
    </div>
  );
}

function Overview() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [certs, setCerts] = useState([]);
  useEffect(() => { (async () => {
    const [t, c, ce] = await Promise.all([api.get("/tasks"), api.get("/freelancer/clients"), api.get("/certificates")]);
    setTasks(t.data); setClients(c.data); setCerts(ce.data);
  })(); }, []);
  return (
    <div className="space-y-6">
      <div className="card-soft p-8" style={{ background: "linear-gradient(135deg,#155EEF 0%,#0B3FB8 100%)" }}>
        <div className="text-white/70 text-sm">Hey there</div>
        <h2 className="mt-1 font-heading font-bold text-3xl text-white">{user?.name}</h2>
        <p className="mt-2 text-white/80 max-w-2xl">Sharpen your skills, deliver great work and grow your earnings.</p>
        <div className="mt-6 flex gap-3">
          <Link to="/academy"><Button data-testid="fl-ov-train" className="btn-orange hover:bg-[#FF6A00]">Go to Academy</Button></Link>
          <Button onClick={() => (window.location.hash = "tasks")} variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20">View tasks</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Clients" value={clients.length} />
        <KPI label="Active tasks" value={tasks.filter(t => t.status !== "done").length} tint="orange" />
        <KPI label="Certificates" value={certs.length} />
        <KPI label="Earnings" value={`₹${(user?.earnings || 0).toLocaleString()}`} tint="orange" />
      </div>
    </div>
  );
}

function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "", phone: user?.phone || "", city: user?.city || "",
    bio: user?.bio || "", skills: (user?.skills || []).join(", "),
  });
  const save = async () => {
    const payload = { ...f, skills: f.skills.split(",").map(s => s.trim()).filter(Boolean) };
    const { data } = await api.patch("/auth/me", payload);
    setUser(data); localStorage.setItem("sellexr_user", JSON.stringify(data));
    toast.success("Profile updated");
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-soft p-8 space-y-4">
        <h3 className="font-heading font-bold text-lg">Your details</h3>
        {[["Name", "name"], ["Phone", "phone"], ["City", "city"]].map(([lbl, k]) => (
          <div key={k}>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{lbl}</label>
            <Input data-testid={`fl-profile-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" />
          </div>
        ))}
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Bio</label>
          <Textarea data-testid="fl-profile-bio" value={f.bio} onChange={e => setF({ ...f, bio: e.target.value })} rows={4} className="mt-1.5" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Skills (comma separated)</label>
          <Input data-testid="fl-profile-skills" value={f.skills} onChange={e => setF({ ...f, skills: e.target.value })} placeholder="Amazon SEO, Product Listing, Inventory" className="mt-1.5" />
        </div>
        <Button onClick={save} className="btn-orange hover:bg-[#FF6A00]" data-testid="fl-profile-save">Save</Button>
      </div>
      <div className="card-soft p-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-2xl" style={{ background: user?.avatar_color || "#FF6A00" }}>{user?.name?.[0]}</div>
        <div className="mt-4 font-heading font-bold text-lg">{user?.name}</div>
        <div className="text-sm text-slate-500">★ {user?.rating || 0} · ₹{(user?.earnings || 0).toLocaleString()} earned</div>
        <Badge className="mt-3 bg-[#FF6A00]/10 text-[#FF6A00] hover:bg-[#FF6A00]/10">Freelancer</Badge>
      </div>
    </div>
  );
}

function TrainingLink() {
  return (
    <div className="card-soft p-10 text-center">
      <GraduationCap size={48} className="mx-auto text-brand" />
      <h3 className="mt-4 font-heading font-bold text-2xl">Head to SELLEXR Academy</h3>
      <p className="mt-2 text-slate-600 max-w-md mx-auto">Watch video courses, take quizzes and earn certificates to unlock more clients.</p>
      <Link to="/academy"><Button className="mt-6 btn-orange hover:bg-[#FF6A00]" data-testid="fl-goto-academy">Open Academy</Button></Link>
    </div>
  );
}

function Certs() {
  const [certs, setCerts] = useState([]);
  useEffect(() => { api.get("/certificates").then(r => setCerts(r.data)); }, []);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {certs.length === 0 && <div className="text-slate-500 col-span-3">No certificates yet. Complete Academy courses to earn some.</div>}
      {certs.map(c => (
        <div key={c.id} className="card-soft p-6 relative overflow-hidden" data-testid={`cert-${c.id}`}>
          <div className="absolute top-0 right-0 w-24 h-24 bg-[#FF6A00]/10 rounded-full -mr-8 -mt-8" />
          <Award className="text-[#FF6A00]" size={32} />
          <div className="mt-4 font-heading font-bold">{c.course_title}</div>
          <div className="text-sm text-slate-500 mt-1">Score: {c.score}%</div>
          <div className="text-xs text-slate-400 mt-1">Issued {new Date(c.issued_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );
}

function Clients() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/freelancer/clients").then(r => setList(r.data)); }, []);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.length === 0 && <div className="text-slate-500 col-span-3">No clients yet. Complete your profile so shopkeepers can find you.</div>}
      {list.map(a => (
        <div key={a.id} className="card-soft p-6" data-testid={`fl-client-${a.id}`}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: a.shopkeeper?.avatar_color || "#155EEF" }}>{a.shopkeeper?.name?.[0]}</div>
          <div className="mt-3 font-heading font-bold">{a.shopkeeper?.store_name || a.shopkeeper?.name}</div>
          <div className="text-sm text-slate-500">{a.shopkeeper?.city || "—"}</div>
          <div className="mt-3 flex flex-wrap gap-1">
            {(a.shopkeeper?.marketplaces || []).map(m => <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const load = () => api.get("/tasks").then(r => setTasks(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, status) => { await api.patch(`/tasks/${id}`, { status }); toast.success("Updated"); load(); };
  const cols = [["todo", "To do"], ["in_progress", "In progress"], ["done", "Done"]];
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {cols.map(([k, l]) => (
        <div key={k} className="card-soft p-4">
          <div className="font-heading font-bold mb-3">{l} <Badge variant="secondary" className="ml-2">{tasks.filter(t => t.status === k).length}</Badge></div>
          <div className="space-y-2">
            {tasks.filter(t => t.status === k).map(t => (
              <div key={t.id} className="border border-slate-200 rounded-lg p-3" data-testid={`fl-task-${t.id}`}>
                <div className="font-semibold text-sm">{t.title}</div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>
                <Badge className={`mt-2 text-xs ${t.priority === "high" ? "bg-red-100 text-red-700" : t.priority === "low" ? "bg-slate-100 text-slate-600" : "bg-[#FF6A00]/10 text-[#FF6A00]"}`}>{t.priority}</Badge>
                <div className="mt-2 flex gap-1">
                  {cols.filter(([kk]) => kk !== k).map(([kk, ll]) => (
                    <Button key={kk} onClick={() => update(t.id, kk)} variant="outline" size="sm" className="text-xs h-7" data-testid={`fl-move-${t.id}-${kk}`}>{ll}</Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Earnings() {
  const [list, setList] = useState([]);
  const { user } = useAuth();
  useEffect(() => { api.get("/payments").then(r => setList(r.data)); }, []);
  return (
    <div className="space-y-6">
      <div className="card-soft p-8" style={{ background: "linear-gradient(135deg,#FFF7ED 0%,#FFEDD5 100%)", borderColor: "#FED7AA" }}>
        <div className="text-sm text-[#FF6A00] uppercase tracking-wider font-semibold">Total earned</div>
        <div className="mt-2 font-heading font-bold text-5xl text-[#FF6A00]">₹{(user?.earnings || 0).toLocaleString()}</div>
      </div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Date", "Amount", "Note"].map(h => <th key={h} className="text-left p-4 text-xs uppercase tracking-wider text-slate-500 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {list.map(p => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="p-4 text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-4 font-semibold text-[#FF6A00]">₹{p.amount}</td>
                <td className="p-4 text-slate-500">{p.note || "—"}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-slate-500">No earnings yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Ratings() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`/freelancers/${user.id}/ratings`).then(r => setList(r.data)); }, [user.id]);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="card-soft p-8 text-center">
        <div className="font-heading font-bold text-6xl text-[#FF6A00]">{user?.rating || 0}</div>
        <div className="flex justify-center mt-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={20} className={i < Math.round(user?.rating || 0) ? "fill-[#FF6A00] text-[#FF6A00]" : "text-slate-300"} />)}</div>
        <div className="mt-2 text-sm text-slate-500">{list.length} reviews</div>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {list.length === 0 && <div className="text-slate-500 card-soft p-6">No reviews yet.</div>}
        {list.map(r => (
          <div key={r.id} className="card-soft p-5" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold">{r.reviewer_name}</div>
              <div className="flex gap-1">{Array.from({ length: Math.round(r.rating) }).map((_, i) => <Star key={i} size={14} className="fill-[#FF6A00] text-[#FF6A00]" />)}</div>
            </div>
            <p className="mt-2 text-sm text-slate-600">{r.review || "No comment"}</p>
            <div className="text-xs text-slate-400 mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
